<script setup lang="ts">
import Button from "primevue/button";
import { computed, ref } from "vue";
import { useToast } from "vue-toast-notification";
import { useSubmissionFilePresign } from "@/components/MyStudents/Submissions/composables/useSubmissionFilePresign";
import { DOWNLOAD_PERMISSION_MESSAGE } from "@/components/MyStudents/Submissions/constants";
import DownloadIcon from "@/components/shared/icons/DownloadIcon.vue";
import { hasTodoDowloadSubmissionDocPermission } from "@/composables/authorization.js";
import { ERROR_TIMEOUT } from "@/constants.js";
import type { SubmissionDownloadButtonProps } from "../../types";
import { downloadFileFromUrl, getSubmissionFileName } from "../../utils";
import { downloadButtonPt } from "./style";

defineOptions({
  name: "submissionDownloadButton",
});

const props = withDefaults(defineProps<SubmissionDownloadButtonProps>(), {
  file: undefined,
  submissionId: undefined,
  dataTestid: "my-students-submission-download",
  disabled: false,
});

const toast = useToast();

const { mutateAsync: presignDownload } = useSubmissionFilePresign();

const isDownloading = ref<boolean>(false);

const canDownload = computed<boolean>(() => !!hasTodoDowloadSubmissionDocPermission.value);

// A disabled button emits no mouse events, so the tooltip sits on a wrapper.
const tooltip = computed(() => ({
  value: DOWNLOAD_PERMISSION_MESSAGE,
  disabled: canDownload.value,
  escape: true,
  class: "bg-white shadow rounded",
  pt: {
    text: "!max-w-[220px] !p-3 !leading-snug !text-indigo-base text-balance",
  },
}));

// Mints its own URL rather than reusing the preview's: the backend records one
// access row per mint, so a reused URL would leave the download unrecorded.
const download = async (): Promise<void> => {
  if (!props.file?.bucket || !props.file?.key) {
    return;
  }
  isDownloading.value = true;
  try {
    const url = await presignDownload({
      bucket: props.file.bucket,
      key: props.file.key,
      submissionId: props.submissionId,
    });
    if (!url) {
      throw new Error("Presign returned no url");
    }
    await downloadFileFromUrl({ url, fileName: getSubmissionFileName(props.file) });
  } catch {
    toast.open({
      message: "Error while downloading the file. Please, try it later.",
      type: "error",
      position: "bottom",
      duration: ERROR_TIMEOUT,
    });
  } finally {
    isDownloading.value = false;
  }
};
</script>

<template>
  <span v-tooltip.top="tooltip">
    <Button
      type="button"
      :disabled="disabled || !canDownload || isDownloading"
      :loading="isDownloading"
      :pt="downloadButtonPt"
      :data-testid="dataTestid"
      @click="download"
    >
      <DownloadIcon custom-class="w-4 h-4 mr-2 fill-current" />
      Download
    </Button>
  </span>
</template>
